import { faAngleDown, faCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import type { ComponentProps, ReactNode, Ref } from 'react';
import { useState } from 'react';
import styled, { css } from 'styled-components';

import { Button } from 'atoms/buttons/Button';
import { IconButton } from 'atoms/buttons/IconButton';
import { Dropdown } from 'atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from 'atoms/icons';
import { Badge } from 'atoms/ui/Badge';

const ButtonsWrapper = styled.div<{ fullWidth: boolean }>`
  position: relative;
  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `}
`;

const StyledIcon = styled(FontAwesomeIcon)<{ $isMenuOpen: boolean }>`
  opacity: 0.5;
  transition: transform 0.25s ease-out;
  transform: none;
  ${({ $isMenuOpen }) =>
    $isMenuOpen &&
    css`
      transform: rotate(-180deg);
    `};
`;

const MainButton = styled(Button)`
  display: flex;
  justify-content: space-between;
`;

const LabelWrapper = styled.span<{ filterSelected: boolean }>`
  overflow: hidden;
  ${({ filterSelected }) =>
    filterSelected &&
    css`
      margin-right: calc(3 * var(--unit));
    `};
`;

const CloseButton = styled(Button)`
  position: absolute;
  top: 0;
  right: 0;
`;

const CloseContent = styled.span`
  color: var(--c-white);
`;

type TriggerProps = {
  expanded: boolean;
  buttonLabel: ReactNode;
  asIconButton?: boolean;
  filterSelected?: boolean;
  nbFiltersSelected?: number;
  onClearFilter?: () => void;
  ref?: Ref<HTMLButtonElement>;
} & Pick<
  ComponentProps<typeof Button>,
  | 'onClick'
  | 'onMouseEnter'
  | 'onMouseLeave'
  | 'color'
  | 'size'
  | 'fullWidth'
  | 'blur'
  | 'disabled'
>;

type Props = {
  children: ReactNode | ((props: { closeDropdown: () => void }) => ReactNode);
  gap?: 0 | 4 | 8 | 16;
  forceMount?: boolean;
  buttonSize?: TriggerProps['size'];
  buttonColor?: TriggerProps['color'];
  asIconButton?: boolean;
} & Omit<TriggerProps, 'expanded' | 'size' | 'color'>;

const Trigger = ({
  expanded,
  buttonLabel,
  size = 'small',
  color = 'tertiary',
  fullWidth,
  filterSelected,
  nbFiltersSelected,
  onClearFilter,
  blur,
  asIconButton,
  ref,
  ...buttonProps
}: TriggerProps) => {
  if (asIconButton) {
    return (
      <ButtonsWrapper fullWidth={!!fullWidth}>
        <IconButton
          ref={ref}
          {...buttonProps}
          color={color}
          size={size}
          fullWidth={fullWidth}
          disableDebounce
          blur={blur}
        >
          <Badge size="small" badgeContent={nbFiltersSelected}>
            {buttonLabel}
          </Badge>
        </IconButton>
      </ButtonsWrapper>
    );
  }
  return (
    <ButtonsWrapper fullWidth={!!fullWidth}>
      <MainButton
        ref={ref}
        {...buttonProps}
        color={color}
        size={size}
        fullWidth={fullWidth}
        disableDebounce
        blur={blur}
      >
        <LabelWrapper filterSelected={!!filterSelected}>
          {buttonLabel}
        </LabelWrapper>

        {!filterSelected && (
          <StyledIcon $isMenuOpen={expanded} icon={faAngleDown} />
        )}
      </MainButton>
      {!!filterSelected && (
        <CloseButton color="transparent" size={size} onClick={onClearFilter}>
          <CloseContent>
            <FontAwesomeIcon icon={faCircleXmark} />
          </CloseContent>
        </CloseButton>
      )}
    </ButtonsWrapper>
  );
};

export const FilterInDropdown = ({
  buttonLabel,
  buttonSize = 'small',
  buttonColor = 'tertiary',
  asIconButton = false,
  fullWidth = false,
  blur = false,
  filterSelected = false,
  nbFiltersSelected = 0,
  onClearFilter = () => {},
  children,
  gap,
  forceMount,
  disabled,
}: Props) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Dropdown
      fullWidth={fullWidth}
      align="right"
      label={props => (
        <Trigger
          expanded={expanded}
          buttonLabel={buttonLabel}
          size={buttonSize}
          color={buttonColor}
          fullWidth={fullWidth}
          filterSelected={filterSelected}
          nbFiltersSelected={nbFiltersSelected}
          onClearFilter={onClearFilter}
          blur={blur}
          asIconButton={asIconButton}
          disabled={disabled}
          {...props}
        />
      )}
      gap={gap || 16}
      onOpen={() => setExpanded(true)}
      onClose={() => setExpanded(false)}
      forceMount={forceMount}
    >
      {({ closeDropdown }): ReactNode =>
        typeof children === 'function' ? children({ closeDropdown }) : children
      }
    </Dropdown>
  );
};
